import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { ButtonProps } from '@mui/material';

import * as React from 'react';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import { Avatar, Box, Button, Card, Stack, Typography } from '@mui/material';

const recipientListMaxHeight = 220;

const Context = React.createContext<string | null>(null);

function useOutreachDialogTitleId() {
  const titleId = React.useContext(Context);

  if (!titleId) {
    throw new Error('OutreachDialog components must be rendered within OutreachDialog.Root');
  }

  return titleId;
}

export type Status = 'idle' | 'loading' | 'submitting' | 'error';

export interface Recipient {
  id: number;
  name: string;
  appointmentLabel: string;
  providerName?: string;
}

export function Root(props: { children: React.ReactNode; onClose: () => void }) {
  const titleId = React.useId();

  return (
    <Context.Provider value={titleId}>
      <Dialog.Root open onClose={props.onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
        {props.children}
      </Dialog.Root>
    </Context.Provider>
  );
}

export function Header(props: {
  children: React.ReactNode;
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  icon: IconDefinition;
}) {
  const titleId = useOutreachDialogTitleId();

  return (
    <Dialog.Header>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: `${props.color}.main`, color: `${props.color}.contrastText` }}>
          <FontAwesomeScalableIcon icon={props.icon} fontSize="small" />
        </Avatar>

        <Box flex={1}>
          <Dialog.Title id={titleId}>{props.children}</Dialog.Title>
        </Box>
      </Stack>
    </Dialog.Header>
  );
}

export function Content(props: { children: React.ReactNode }) {
  return <Dialog.Content>{props.children}</Dialog.Content>;
}

export function RecipientDetails(props: { recipient: Recipient }) {
  return (
    <Stack direction="row" spacing={2} mb={2}>
      <InfoItem label="Patient" value={props.recipient.name} />

      {props.recipient.providerName ? (
        <InfoItem label="Provider" value={props.recipient.providerName} />
      ) : null}

      <InfoItem label="Appointment" value={props.recipient.appointmentLabel} />
    </Stack>
  );
}

export function MessagePreview(props: { children: React.ReactNode; label: React.ReactNode }) {
  return (
    <Box>
      <Typography component="p" variant="caption" color="text.secondary" textTransform="uppercase">
        {props.label}
      </Typography>

      <Card variant="outlined" sx={{ padding: 1.5, marginTop: 0.5, bgcolor: 'background.default' }}>
        <Typography component="div" variant="body2" whiteSpace="pre-line">
          {props.children}
        </Typography>
      </Card>
    </Box>
  );
}

export function RecipientList(props: { note: React.ReactNode; recipients: Recipient[] }) {
  return (
    <>
      <Typography component="p" variant="caption" color="text.secondary" marginTop={1}>
        {props.note}
      </Typography>

      <Typography
        component="p"
        variant="caption"
        color="text.secondary"
        textTransform="uppercase"
        marginTop={2}
      >
        Recipients
      </Typography>

      <Stack
        component="ul"
        aria-label="Outreach recipients"
        direction="column"
        spacing={0}
        tabIndex={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          listStyle: 'none',
          marginTop: 0.5,
          maxHeight: recipientListMaxHeight,
          overflowY: 'auto',
          padding: 0,
        }}
      >
        {props.recipients.map((recipient) => (
          <Stack
            component="li"
            key={recipient.id}
            direction="row"
            spacing={1}
            alignItems="baseline"
            paddingX={1.5}
            paddingY={1}
            sx={{ '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' } }}
          >
            <Typography component="span" variant="body2">
              {recipient.name}
            </Typography>
            <Typography component="span" variant="caption" color="text.secondary">
              {recipient.appointmentLabel}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </>
  );
}

export function DeliverySummary(props: { children: React.ReactNode }) {
  return (
    <Typography component="p" variant="caption" color="text.secondary" marginTop={2}>
      {props.children}
    </Typography>
  );
}

export function Footer(props: {
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  confirmLabel: string;
  icon: IconDefinition;
  onConfirm: () => void;
  status: Status;
}) {
  return (
    <Dialog.Footer>
      <Dialog.DismissButton>Cancel</Dialog.DismissButton>

      <Button
        variant="contained"
        color={props.color}
        size="large"
        startIcon={<FontAwesomeScalableIcon icon={props.icon} />}
        loading={props.status === 'submitting'}
        loadingPosition="start"
        disabled={props.status !== 'idle'}
        onClick={props.onConfirm}
      >
        {props.confirmLabel}
      </Button>
    </Dialog.Footer>
  );
}

function InfoItem(props: { label: string; value: string }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, padding: 1.5, bgcolor: 'background.default' }}>
      <Typography component="p" variant="caption" color="text.secondary" textTransform="uppercase">
        {props.label}
      </Typography>
      <Typography component="p" variant="body2">
        {props.value}
      </Typography>
    </Card>
  );
}
